import {CommonModule} from '@angular/common';
import {
  Component,
  ContentChild,
  DestroyRef,
  inject,
  input,
  OnInit,
  TemplateRef,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  ControlContainer,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import {MatIconModule} from '@angular/material/icon';
import {
  ConditionalStatement,
  ConditionalStatementList,
  LogicalOperator,
} from '@em/shared/mapping-engine/domain-models';
import {EmBoxModule, EmButtonModule} from '@em/shared/ui';
import {Observable} from 'rxjs';
import {map, shareReplay, startWith} from 'rxjs/operators';
import {MappingEngineFormBuilderService} from '../../services/mapping-engine-form-builder/mapping-engine-form-builder.service';
import {EmConditionalStatementComponent} from '../conditional-statement/conditional-statement.component';

const AllAvailableOperators: LogicalOperator[] = ['and', 'or'];

@Component({
  selector: 'em-conditional-statement-list',
  templateUrl: './conditional-statement-list.component.html',
  styleUrls: [
    './conditional-statement-list.component.scss',
    '../../styles/mapping-engine.scss',
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    EmButtonModule,
    EmConditionalStatementComponent,
    EmBoxModule,
  ],
})
export class EmConditionalStatementListComponent implements OnInit {
  private readonly _controlContainer = inject(ControlContainer);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _fb = inject(MappingEngineFormBuilderService);
  @ContentChild(TemplateRef) template!: TemplateRef<unknown>;

  allowEmpty = input<boolean>(false);
  formGroup: UntypedFormGroup | null | undefined;
  availableOperators$: Observable<LogicalOperator[]> | undefined;
  outerAvailableOperators$: Observable<LogicalOperator[]> | undefined;

  get conditionalStatementsFormArray(): UntypedFormArray {
    return this.formGroup?.get('conditionalStatements') as UntypedFormArray;
  }

  get logicalOperatorControl(): UntypedFormControl {
    return this.formGroup?.get('logicalOperator') as UntypedFormControl;
  }

  get canDeleteConditions(): boolean {
    const statements: ConditionalStatement[] | undefined =
      this.conditionalStatementsFormArray?.value;
    if (
      statements &&
      (statements.length > 1 || statements[0].conditions?.length > 1)
    ) {
      return true;
    } else {
      return false;
    }
  }

  ngOnInit(): void {
    this.formGroup = this._controlContainer.control as UntypedFormGroup;

    (this.formGroup.valueChanges as Observable<ConditionalStatementList>)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((statementsList) => {
        // remove statements with empty conditions
        statementsList?.conditionalStatements.forEach((statement, index) =>
          this.removeIfEmpty(statement, index),
        );
      });

    this.availableOperators$ = (
      this.formGroup.valueChanges as Observable<ConditionalStatementList>
    ).pipe(
      startWith(this.formGroup.value),
      map((statementsList) => {
        // if operator is defined, use it to calculate the avialable operators
        if (statementsList?.logicalOperator) {
          return AllAvailableOperators.filter(
            (op) => op !== statementsList.logicalOperator,
          );
        }

        // check if any statement already define its operator, then use it as the available
        let innerOperator: LogicalOperator | undefined;
        if (statementsList?.conditionalStatements) {
          for (const statement of statementsList.conditionalStatements) {
            if (statement.logicalOperator) {
              innerOperator = statement.logicalOperator;
              break;
            }
          }
        }

        return innerOperator
          ? AllAvailableOperators.filter((op) => op === innerOperator)
          : AllAvailableOperators;
      }),
      shareReplay({refCount: false, bufferSize: 1}),
    );

    this.outerAvailableOperators$ = this.availableOperators$.pipe(
      map((innerOperators) =>
        AllAvailableOperators.filter((op) => innerOperators.indexOf(op) === -1),
      ),
    );
  }

  addNewStatement(logicalOperator: LogicalOperator | undefined) {
    if (!this.logicalOperatorControl.value && logicalOperator) {
      this.logicalOperatorControl.setValue(logicalOperator);
    }

    this._fb.addNewConditionalStatement(this.conditionalStatementsFormArray);
  }

  removeStatement(index: number) {
    this.conditionalStatementsFormArray.removeAt(index);
    if (this.conditionalStatementsFormArray.length <= 1) {
      this.logicalOperatorControl.setValue(null);
    }
  }

  removeIfEmpty(statement: ConditionalStatement, index: number) {
    if (!statement?.conditions.length) {
      this.removeStatement(index);
    }
  }
}
